/**
 * URL Encoded Type Detector
 * 
 * Detects URL-encoded strings (percent-encoding).
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { ITypeDetector } from '@/interfaces/ITypeDetector';

export const UrlEncodedDetector: ITypeDetector = {
  id: 'url-encoded-detector',
  name: 'URL Encoded Detector',
  priority: 75, // Below URL but above Base64
  detectedType: SEMANTIC_TYPES.URL_ENCODED,

  detect(input: string): ISemanticType | null {
    const trimmed = input.trim();
    
    if (trimmed.length < 3) {
      return null;
    }

    // Check for percent-encoded characters
    // Must have at least one %XX pattern
    const percentPattern = /%[0-9A-Fa-f]{2}/g;
    const matches = trimmed.match(percentPattern);
    
    if (!matches || matches.length === 0) {
      return null;
    }

    // Calculate ratio of encoded characters
    // URL-encoded strings typically have significant encoding
    const encodedLength = matches.length * 3;
    const ratio = encodedLength / trimmed.length;

    // If at least 10% of the string is percent-encoded, it's likely URL-encoded
    if (ratio >= 0.1) {
      // Make sure it's not a full URL (those should be handled by URL detector)
      if (trimmed.match(/^https?:\/\//i)) {
        return null;
      }
      
      // Query string format: key=value&key2=value2
      if (/^[\w%]+=[\w%]*(&[\w%]+=[\w%]*)*$/.test(trimmed)) {
        return SEMANTIC_TYPES.URL_ENCODED;
      }

      // Just encoded content
      if (ratio >= 0.2) {
        return SEMANTIC_TYPES.URL_ENCODED;
      }
    }

    return null;
  },
};
